import React, { useState, useContext } from "react";
import "./GameThumbnailComponent.scss";
import { gameLaunchUrl } from "../../apis/userDetailsApi";
import { demoLogin } from "../../apis/authApis";
import { setResponse } from "../../apis/util";
import { ProfileContext } from "../../contexts/APIProvider";
import { Dialog } from "../Modal";
import GuestPlayModal from "../GuestPlayModal/GuestPlayModal";
import { useScrollReveal } from "../../motion/useScrollReveal";

// Game detail type
interface GameDetail {
  id: string;
  imageSource: string;
  title?: string;
  className?: string;
  customToken?: string;
  isComingSoon?: boolean;
}

interface GameThumbnailProps {
  clickEvent: (id: string, url: string) => void;
  setGameState: (state: boolean) => void;
  gamesList: GameDetail[];
  setLaunchUrl: (url: string) => void;
}

const GameThumbnailComponent: React.FC<GameThumbnailProps> = ({
  clickEvent,
  setGameState,
  gamesList,
  setLaunchUrl,
}) => {
  const { setProfile, setLoggedIn } = useContext(ProfileContext);
  const [openModel, setOpenModel] = useState(false);
  const [show, setShow] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState<GameDetail | null>(null);

  // Step 2 of the entrance: thumbnails slide in from the right, one after
  // another. The `delay` holds them back until the tab bar (step 1, in
  // Game.tsx) has risen in, so the two reveals play in sequence rather than
  // clashing. Re-runs on every gamesList change so it replays on tab switch.
  // Reduced motion is respected by the hook itself.
  const gridRef = useScrollReveal<HTMLDivElement>({
    selector: ".GameThumbnailComponent",
    x: 120,
    stagger: 0.08,
    delay: 0.6,
    deps: [gamesList],
  });

  const handleClose = () => {
    setShow(false);
    setOpenModel(false);
    setShowGuestModal(false);
  };

  const getGameUrl = async (
    gameDetail: GameDetail
  ): Promise<string | undefined> => {
    try {
      const response = await gameLaunchUrl({
        id: gameDetail?.id,
        currency: "",
        customToken: gameDetail?.customToken ?? undefined,
      });
      return response?.url;
    } catch (error) {
      console.error("Error fetching game URL:", error);
      return undefined;
    }
  };

  const onPlay = (gameDetail: GameDetail) => {
    if (gameDetail.isComingSoon) return;

    const isUserAvailable = sessionStorage.getItem("token");
    if (isUserAvailable) {
      setGameState(true);
      getGameUrl(gameDetail)
        .then((url) => {
          if (url) {
            setLaunchUrl(url);
            clickEvent(gameDetail.id, url);
          }
        })
        .catch((error) => console.error(error));
    } else {
      setSelectedGame(gameDetail);
      setShowGuestModal(true);
    }
  };

  const handleDemoPlay = async (): Promise<boolean> => {
    try {
      const response = await demoLogin();
      setResponse(response);
      setProfile(response);
      setLoggedIn(true);

        setGameState(true);
        const url = await getGameUrl({
        ...selectedGame!,
          customToken: response?.token,
        });

        if (url) {
          setLaunchUrl(url);
        clickEvent(selectedGame!.id, url);
        setShowGuestModal(false);
      } else {
        console.error("No game URL received, keeping modal open.");
    }

      setShowGuestModal(false);
      return true;
    } catch (error) {
      console.error("Demo login failed:", error);
      return false;
    }
  };

  const handleSignIn = () => {
    setShowGuestModal(false);
    setOpenModel(true);
    setShow(true);
  };

  return (
    <div className="container-game-thumbnail">
      <div className="Gamescontainer" ref={gridRef}>
      {gamesList?.length > 0 ? (
        gamesList.map((gameDetail) => (
          <div className="GameThumbnailComponent" key={gameDetail.id}>
            <button
              type="button"
              className={`game-container clip-circle ${
                gameDetail.isComingSoon ? "disabled-game" : ""
              }`}
              onClick={() => onPlay(gameDetail)}
              aria-label="Play game"
              disabled={gameDetail.isComingSoon}
            >
              <img
                className="image"
                width={258}
                height={338}
                src={gameDetail.imageSource}
                alt="Game Thumbnail"
              />

              <div className="title-container">
                {gameDetail.title && (
                <img
                  className={`game-title ${gameDetail.className || ""}`}
                  src={gameDetail.title}
                  alt="Game Title"
                />
                )}
              </div>

              {gameDetail.isComingSoon && (
                <div className="coming-soon-overlay">Coming Soon!</div>
              )}
            </button>
          </div>
        ))
      ) : (
        <p>No games available.</p>
      )}

      {openModel && <Dialog show={show} handleClose={handleClose} />}

      <GuestPlayModal
        show={showGuestModal}
        handleClose={handleClose}
        handleDemoPlay={handleDemoPlay}
        handleSignIn={handleSignIn}
      />
      </div>
    </div>
  );
};

export default GameThumbnailComponent;
